import { V6Client } from "@aws-amplify/api-graphql";
import {
  ADLFieldSectionMapping,
  ADLFieldSectionMappingInput,
  FieldTemplate,
  FieldTemplateType,
  ModelSortDirection,
} from "~/api/API";
import {
  createFieldTemplateVersion,
  updateDoctorReturningID,
  updateFieldTemplateRemoveLatestMarker,
} from "~/api/graphql-custom/mutations";
import {
  getFieldTemplateOptimized,
  listFieldTemplatesByDoctorIdLatest,
} from "~/api/graphql-custom/queries";
import { amplifyGraphQLClient } from "~/lib/amplify-client";

/**
 * Service for managing FieldTemplate entities in Phase 2 dual-write mode.
 *
 * This service handles:
 * - Fetching the latest template version for a doctor
 * - Creating new template versions when doctor preferences change
 * - Managing the latestMarker sparse index for version tracking
 */
class FieldTemplateService {
  private static instance: FieldTemplateService;
  private client: V6Client<never>;

  private constructor(client: V6Client<never>) {
    this.client = client;
  }

  static getInstance(
    client: V6Client<never> = amplifyGraphQLClient,
  ): FieldTemplateService {
    if (!FieldTemplateService.instance) {
      FieldTemplateService.instance = new FieldTemplateService(client);
    }
    return FieldTemplateService.instance;
  }

  /**
   * Get a specific template by ID and version.
   * Used when loading an appointment that has a fieldTemplateRef.
   */
  async getFieldTemplate(
    id: string,
    version: number,
  ): Promise<FieldTemplate | null> {
    const response = await this.client.graphql({
      query: getFieldTemplateOptimized,
      variables: { id, version },
    });
    return response.data.getFieldTemplate as FieldTemplate | null;
  }

  /**
   * Get the latest template version for a doctor.
   * Uses the byDoctorId GSI with descending version sort.
   */
  async getLatestDoctorTemplate(
    doctorId: string,
  ): Promise<FieldTemplate | null> {
    const response = await this.client.graphql({
      query: listFieldTemplatesByDoctorIdLatest,
      variables: {
        doctorId,
        sortDirection: ModelSortDirection.DESC,
        limit: 1,
      },
    });
    const items = response.data.fieldTemplatesByDoctorIdAndVersion?.items ?? [];
    return (items[0] as FieldTemplate | null) ?? null;
  }

  /**
   * Create a new template version or the first template for a doctor.
   *
   * This method handles two scenarios:
   * 1. Creating a new version of an existing template:
   *    - Removes latestMarker from the current version (takes it out of sparse index)
   *    - Creates a new version with latestMarker="LATEST"
   * 2. Creating the first template for a doctor (when currentTemplate is null):
   *    - Creates version 1 with latestMarker="LATEST"
   *    - Updates Doctor.fieldTemplateId to link the doctor to the new template
   *
   * Note: In a production system, these two operations should ideally be in a
   * transaction. For now, we do them sequentially and handle the case where
   * the new version is always "latest" by version number (sorted DESC).
   *
   * @returns Object containing the new template and whether it was the first template created
   */
  async createOrUpdateTemplate(params: {
    currentTemplate: FieldTemplate | null;
    doctorId: string;
    templateType: FieldTemplateType;
    adlFieldSectionMappings: (
      | ADLFieldSectionMapping
      | ADLFieldSectionMappingInput
    )[];
    medicalHistoryFieldIDs: (string | null)[];
    changeDescription?: string;
  }): Promise<{ template: FieldTemplate; isFirstTemplate: boolean }> {
    const {
      currentTemplate,
      doctorId,
      templateType,
      adlFieldSectionMappings,
      medicalHistoryFieldIDs,
      changeDescription,
    } = params;

    const isFirstTemplate = !currentTemplate;

    // If there's an existing template, remove its latestMarker first
    if (currentTemplate) {
      await this.client.graphql({
        query: updateFieldTemplateRemoveLatestMarker,
        variables: {
          input: {
            id: currentTemplate.id,
            version: currentTemplate.version,
            latestMarker: null,
          },
        },
      });
    }

    // Determine version and ID for the new template
    const newVersion = currentTemplate ? currentTemplate.version + 1 : 1;
    const templateId = currentTemplate?.id ?? crypto.randomUUID();

    const response = await this.client.graphql({
      query: createFieldTemplateVersion,
      variables: {
        input: {
          id: templateId,
          version: newVersion,
          templateType,
          doctorId,
          adlFieldSectionMappings: adlFieldSectionMappings.map((m) => ({
            ADLFieldSectionID: m.ADLFieldSectionID,
            ADLFieldIDs: m.ADLFieldIDs,
          })),
          medicalHistoryFieldIDs: medicalHistoryFieldIDs.filter(
            (id): id is string => id !== null,
          ),
          latestMarker: "LATEST",
          changeDescription,
        },
      },
    });

    const newTemplate = response.data.createFieldTemplate as FieldTemplate;

    // If this is the first template, update the doctor's fieldTemplateId
    if (isFirstTemplate) {
      await this.updateDoctorFieldTemplateId(doctorId, newTemplate.id);
    }

    return { template: newTemplate, isFirstTemplate };
  }

  /**
   * Get the template reference (id + version) for a doctor.
   * Returns null if the doctor has no FieldTemplate.
   *
   * Used when creating new appointments to set fieldTemplateRef.
   */
  async getTemplateRefForDoctor(
    doctorId: string,
  ): Promise<{ id: string; version: number } | null> {
    const template = await this.getLatestDoctorTemplate(doctorId);
    if (template) {
      return { id: template.id, version: template.version };
    }
    return null;
  }

  /**
   * Update the doctor's fieldTemplateId to link them to a template.
   * This should be called when creating the first template for a doctor.
   */
  async updateDoctorFieldTemplateId(
    doctorId: string,
    fieldTemplateId: string,
  ): Promise<void> {
    await this.client.graphql({
      query: updateDoctorReturningID,
      variables: {
        input: {
          id: doctorId,
          fieldTemplateId,
        },
      },
    });
  }
}

export const fieldTemplateService = FieldTemplateService.getInstance();
